import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { downloadPdf } from "@/lib/downloadPdf";
import { formatCurrency as fmtCurrency } from "@/lib/formatters";
import { useNoIndex } from "@/lib/useNoIndex";  // PR #97 / R8
import {
  FileText, 
  Download, 
  Plus, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  ChevronRight,
  LogOut,
  Home,
  FolderOpen
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_HORIZONTAL = "/logos/solara-horizontal.png";

export default function MyApplications() {
  useNoIndex();
  const { user, logout } = useAuth();
  const [applications, setApplications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchApplications();
    // APPLY-3 (2026-07-08) — refetch when the user returns to this tab (e.g.
    // straight after submitting an application in another flow) so the list
    // and status badges are never stale (LOW: no auto-refresh post-submit).
    const onVisible = () => { if (!document.hidden) fetchApplications(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await axios.get(`${API}/my-applications`);
      setApplications(response.data.applications || []);
    } catch (error) {
      console.error("Error fetching applications:", error);
      toast.error("Failed to load applications");
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: "bg-gray-100 text-gray-600", icon: Clock, label: "Draft" },
      new: { color: "bg-slate-100 text-slate-700", icon: Clock, label: "New" },
      application_completed: { color: "bg-blue-100 text-blue-700", icon: CheckCircle, label: "Application Completed" },
      assessment: { color: "bg-violet-100 text-violet-700", icon: Clock, label: "Assessment" },
      submitted_to_lender: { color: "bg-amber-100 text-amber-700", icon: Clock, label: "Submitted to Lender" },
      indicative_terms: { color: "bg-cyan-100 text-cyan-700", icon: Clock, label: "Indicative Terms" },
      conditions_precedent: { color: "bg-pink-100 text-pink-700", icon: Clock, label: "Conditions Precedent" },
      mortgage_docs_issued: { color: "bg-teal-100 text-teal-700", icon: CheckCircle, label: "Mortgage Docs Issued" },
      settlement: { color: "bg-green-100 text-green-700", icon: CheckCircle, label: "Settlement" },
      complete: { color: "bg-emerald-100 text-emerald-700", icon: CheckCircle, label: "Complete" },
      submitted: { color: "bg-blue-100 text-blue-700", icon: Clock, label: "Submitted" },
      under_review: { color: "bg-yellow-100 text-yellow-700", icon: Clock, label: "Under Review" },
      approved: { color: "bg-green-100 text-green-700", icon: CheckCircle, label: "Approved" },
      rejected: { color: "bg-red-100 text-red-700", icon: AlertCircle, label: "Rejected" },
    };
    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatCurrency = (value) => {
    return fmtCurrency(value, 0);
  };

  return (
    <div className="min-h-screen bg-[#fafaf9]">
      {/* Header */}
      <header className="bg-[#021d49] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <img 
                src={LOGO_HORIZONTAL} 
                alt="Solara Global" 
                className="h-10 brightness-0 invert"
              />
            </Link>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-300 hidden sm:block">{user?.name}</span>
              <Link to="/" className="text-gray-300 hover:text-white">
                <Home className="w-5 h-5" />
              </Link>
              <button onClick={logout} className="text-gray-300 hover:text-white">
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-[#021d49]">My Applications</h1>
            <p className="text-gray-500 mt-1">View and manage your loan applications</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/portal"
              className="btn-press-instant bg-white border border-gray-200 hover:border-[#bd9a59] text-[#021d49] px-6 py-3 rounded font-medium flex items-center justify-center gap-2 transition-colors"
              data-testid="document-portal-btn"
            >
              <FolderOpen className="w-5 h-5" />
              Document Portal
            </Link>
            {/* PR HF-Apply-2 — force a fresh application (don't auto-resume
                the most-recent draft, which surprised users into editing the
                wrong record when they actually wanted a new one).
                PR HF-Apply-3 — btn-press-instant for instant click feedback. */}
            <Link
              to="/apply?new=1"
              className="btn-press-instant bg-[#bd9a59] hover:bg-[#9c7e46] text-white px-6 py-3 rounded font-medium flex items-center justify-center gap-2 transition-colors"
              data-testid="new-application-btn"
            >
              <Plus className="w-5 h-5" />
              New Application
            </Link>
          </div>
        </div>

        {/* Applications List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-[#bd9a59] border-t-transparent rounded-full" />
          </div>
        ) : applications.length === 0 ? (
          <div className="bg-white rounded shadow-sm border border-gray-200 p-12 text-center">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-700 mb-2">No Applications Yet</h3>
            <p className="text-gray-500 mb-6">Start your first loan application today</p>
            <Link
              to="/apply"
              className="bg-[#021d49] hover:bg-[#052b66] text-white px-6 py-3 rounded font-medium inline-flex items-center gap-2 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Start Application
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {applications.map((app) => (
              <div
                key={app.id}
                className="bg-white rounded shadow-sm border border-gray-200 p-4 sm:p-6 hover:shadow-md transition-shadow"
                data-testid={`application-card-${app.id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-mono text-sm text-[#bd9a59] font-medium">
                        {app.application_reference}
                      </span>
                      {getStatusBadge(app.status)}
                    </div>
                    <h3 className="font-medium text-[#021d49]">
                      {app.company_borrower?.company_name || "Company Not Specified"}
                    </h3>
                    <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-500">
                      <span>Loan: {formatCurrency(app.loan_details?.net_loan_required)}</span>
                      <span>Created: {formatDate(app.created_at)}</span>
                      {app.submitted_at && <span>Submitted: {formatDate(app.submitted_at)}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => downloadPdf(`${API}/applications/${app.id}/pdf`, `Solara_Application_${app.application_reference}.pdf`)}
                      className="p-2 text-gray-500 hover:text-[#bd9a59] hover:bg-gray-100 rounded transition-colors"
                      title="Download PDF"
                      data-testid={`download-pdf-${app.id}`}
                    >
                      <Download className="w-5 h-5" />
                    </button>
                    {/* PR HF-Apply-2 — show "Continue" for any unsubmitted
                        application. Previously gated by `app.status === "draft"`,
                        but post-Spine-1a the status field holds substage ids
                        like "lead_new" / "lead_application_completed", so the
                        equality check never matched and the button vanished.
                        `!app.submitted_at` is the canonical not-yet-submitted
                        marker and survives any future status-vocabulary change.
                        Links to /apply?resume=<id> so the form loads THIS
                        specific draft, not the most-recent one (matters when
                        the user has multiple drafts open). */}
                    {(!app.submitted_at || (app.client_edit_unlocked_until && new Date(app.client_edit_unlocked_until) > new Date())) && (
                      <Link
                        to={`/apply?resume=${encodeURIComponent(app.id)}`}
                        className="btn-press-instant bg-[#021d49] hover:bg-[#052b66] text-white px-4 py-2 rounded text-sm font-medium flex items-center gap-1 transition-colors"
                      >
                        Continue
                        <ChevronRight className="w-4 h-4" />
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
